import React, { useState, useEffect } from "react";
import { TextField, Button, Container, Grid, MenuItem } from "@mui/material";

interface FormData {
  Date: Date;
  Start_time: Date;
  Finish_time: Date;
  Staff_Name: string;
  Parent_Service_Code: string;
  Parent_Service: string;
  Actual_Provided_Service: string;
  Measure: string;
  Quantity: number;
  Price: number;
  Status: string;
}

const initialFormData: FormData = {
  Date: new Date(),
  Start_time: new Date(),
  Finish_time: new Date(),
  Staff_Name: "",
  Parent_Service_Code: "",
  Parent_Service: "",
  Actual_Provided_Service: "",
  Measure: "hrs",
  Quantity: 0,
  Price: 0,
  Status: "paid",
};

const Timesheets: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [statusOptions] = useState<string[]>(["paid", "unpaid"]);
  const [staffNames, setStaffNames] = useState<string[]>([]);
  const [parentServiceCodes, setParentServiceCodes] = useState<string[]>([]);
  const [parentServices, setParentServices] = useState<string[]>([]);
  const [actualProvidedServices, setActualProvidedServices] = useState<
    string[]
  >([]);

  useEffect(() => {
    // Simulated API fetches for dropdown options (replace with actual fetches)
    const fetchStaffNames = async () => {
      const data = ["John Doe", "Jane Smith", "Bob Johnson"]; // Example data
      setStaffNames(data);
    };

    const fetchParentServiceCodes = async () => {
      const data = ["PSC001", "PSC002", "PSC003"]; // Example data
      setParentServiceCodes(data);
    };

    const fetchParentServices = async () => {
      const data = ["Service A", "Service B", "Service C"]; // Example data
      setParentServices(data);
    };

    const fetchActualProvidedServices = async () => {
      const data = ["Service X", "Service Y", "Service Z"]; // Example data
      setActualProvidedServices(data);
    };

    // Trigger API fetches
    fetchStaffNames();
    fetchParentServiceCodes();
    fetchParentServices();
    fetchActualProvidedServices();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange =
    (name: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setFormData({
        ...formData,
        [name]: new Date(value),
      });
    };

  const handleStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      Status: value as string,
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log(formData); // Logging form data for demonstration
    setFormData(initialFormData); // Reset form data after submission
  };

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              name="Date"
              value={formData.Date.toISOString().split("T")[0]}
              onChange={handleDateChange("Date")}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Start Time"
              type="time"
              name="Start_time"
              value={formData.Start_time.toLocaleTimeString("en-US", {
                hour12: false,
              })}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Finish Time"
              type="time"
              name="Finish_time"
              value={formData.Finish_time.toLocaleTimeString("en-US", {
                hour12: false,
              })}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Staff Name"
              name="Staff_Name"
              value={formData.Staff_Name}
              onChange={handleChange}
              variant="outlined"
            >
              {staffNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Parent Service Code"
              name="Parent_Service_Code"
              value={formData.Parent_Service_Code}
              onChange={handleChange}
              variant="outlined"
            >
              {parentServiceCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Parent Service"
              name="Parent_Service"
              value={formData.Parent_Service}
              onChange={handleChange}
              variant="outlined"
            >
              {parentServices.map((service) => (
                <MenuItem key={service} value={service}>
                  {service}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Actual Provided Service"
              name="Actual_Provided_Service"
              value={formData.Actual_Provided_Service}
              onChange={handleChange}
              variant="outlined"
            >
              {actualProvidedServices.map((service) => (
                <MenuItem key={service} value={service}>
                  {service}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Measure"
              name="Measure"
              value={formData.Measure}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              name="Quantity"
              value={formData.Quantity}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Price"
              type="number"
              name="Price"
              value={formData.Price}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Status"
              name="Status"
              value={formData.Status}
              onChange={handleStatusChange}
              variant="outlined"
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" type="submit">
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default Timesheets;
